import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const TIME_SLOTS = [
  '오전 (06-09)',
  '오전 (09-12)',
  '오후 (12-15)',
  '오후 (15-18)',
  '저녁 (18-21)',
  '야간 (21-24)',
  '심야 (00-06)',
];

type ScheduleEntry = {
  id: string;
  schedule_date: string;
  status: 'available' | 'unavailable' | 'booked';
  time_slots: string[] | null;
  notes: string | null;
};

export default function CaregiverSchedulePage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<'available' | 'unavailable'>('available');
  const [editSlots, setEditSlots] = useState<string[]>([]);
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  useEffect(() => {
    if (!auth.userId) return;
    loadSchedules();
  }, [auth.userId, year, month]);

  async function loadSchedules() {
    if (!auth.userId) return;
    setLoading(true);
    try {
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = new Date(year, month + 1, 0);
      const endDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      const { data } = await supabase
        .from('caregiver_schedules')
        .select('*')
        .eq('caregiver_user_id', auth.userId)
        .gte('schedule_date', startDate)
        .lte('schedule_date', endDateStr);

      setSchedules((data || []) as ScheduleEntry[]);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    } finally {
      setLoading(false);
    }
  }

  function getScheduleForDate(dateStr: string): ScheduleEntry | undefined {
    return schedules.find((s) => s.schedule_date === dateStr);
  }

  function prevMonth() {
    setCurrentMonth(new Date(year, month - 1, 1));
  }

  function nextMonth() {
    setCurrentMonth(new Date(year, month + 1, 1));
  }

  function openEdit(dateStr: string) {
    const existing = getScheduleForDate(dateStr);
    setEditingDate(dateStr);
    setEditStatus(existing?.status || 'available');
    setEditSlots(existing?.time_slots || []);
    setEditNote(existing?.notes || '');
  }

  function closeEdit() {
    setEditingDate(null);
    setMessage('');
  }

  function toggleSlot(slot: string) {
    setEditSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  }

  async function handleSave() {
    if (!auth.userId || !editingDate) return;
    setSaving(true);
    setMessage('');
    try {
      const { data: existing } = await supabase
        .from('caregiver_schedules')
        .select('id')
        .eq('caregiver_user_id', auth.userId)
        .eq('schedule_date', editingDate)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('caregiver_schedules')
          .update({
            status: editStatus,
            time_slots: editSlots,
            notes: editNote || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
      } else {
        await supabase.from('caregiver_schedules').insert({
          caregiver_user_id: auth.userId,
          schedule_date: editingDate,
          status: editStatus,
          time_slots: editSlots,
          notes: editNote || null,
        });
      }

      setMessage('저장되었습니다!');
      await loadSchedules();
      setTimeout(() => {
        closeEdit();
      }, 800);
    } catch (err) {
      console.error('Failed to save schedule:', err);
      setMessage('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  }

  function getDaysInMonth(): (number | null)[] {
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= lastDate; d++) days.push(d);
    return days;
  }

  function dateStr(day: number): string {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  const days = getDaysInMonth();

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate('/app/caregiver/home')}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-gray-900">스케줄 관리</h1>
            <p className="text-xs text-gray-400">날짜별 근무 가능 여부를 설정하세요</p>
          </div>
        </div>

        {/* 범례 */}
        <div className="flex items-center gap-4 mb-4">
          {[
            { color: '#C8E6C9', text: '텍스트-[#2E7D6B]', label: '가능' },
            { color: '#FFCDD2', text: '텍스트-[#D32F2F]', label: '불가능' },
            { color: '#BBDEFB', text: '텍스트-[#1976D2]', label: '예약됨' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-md" style={{ backgroundColor: item.color }} />
              <span className="text-[10px] font-medium text-gray-500">{item.label}</span>
            </div>
          ))}
        </div>

        {/* 월 네비게이션 */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
            <i className="ri-arrow-left-s-line text-gray-500" />
          </button>
          <h2 className="text-base font-extrabold text-gray-900">
            {year}년 {month + 1}월
          </h2>
          <button onClick={nextMonth} className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
            <i className="ri-arrow-right-s-line text-gray-500" />
          </button>
        </div>

        {/* 달력 */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-5">
          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 border-b border-gray-100">
            {WEEKDAYS.map((wd) => (
              <div
                key={wd}
                className={`py-2.5 text-center text-[11px] font-extrabold ${
                  wd === '일' ? 'text-red-400' : wd === '토' ? 'text-blue-400' : 'text-gray-500'
                }`}
              >
                {wd}
              </div>
            ))}
          </div>

          {/* 날짜 셀 */}
          <div className="grid grid-cols-7">
            {days.map((day, idx) => {
              if (day === null) {
                return <div key={`empty-${idx}`} className="aspect-square" />;
              }

              const ds = dateStr(day);
              const schedule = getScheduleForDate(ds);
              const isToday = ds === todayStr;
              const isPast = ds < todayStr;

              let bgColor = 'bg-white';
              let statusDot = null;
              if (schedule?.status === 'available') {
                bgColor = 'bg-[#C8E6C9]/60';
                statusDot = <div className="w-1.5 h-1.5 rounded-full bg-[#2E7D6B]" />;
              } else if (schedule?.status === 'unavailable') {
                bgColor = 'bg-[#FFCDD2]/60';
                statusDot = <div className="w-1.5 h-1.5 rounded-full bg-[#D32F2F]" />;
              } else if (schedule?.status === 'booked') {
                bgColor = 'bg-[#BBDEFB]/60';
                statusDot = <div className="w-1.5 h-1.5 rounded-full bg-[#1976D2]" />;
              }

              return (
                <button
                  key={ds}
                  onClick={() => !isPast && openEdit(ds)}
                  disabled={isPast}
                  className={`aspect-square flex flex-col items-center justify-center ${bgColor} border-b border-r border-gray-50 cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-default relative`}
                >
                  <span
                    className={`text-xs font-bold ${
                      isToday
                        ? 'w-6 h-6 rounded-full bg-[#1976D2] text-white flex items-center justify-center'
                        : idx % 7 === 0
                        ? 'text-red-400'
                        : idx % 7 === 6
                        ? 'text-blue-400'
                        : 'text-gray-700'
                    }`}
                  >
                    {day}
                  </span>
                  {statusDot}
                  {schedule?.time_slots && schedule.time_slots.length > 0 && (
                    <span className="text-[8px] text-gray-400 mt-0.5">
                      {schedule.time_slots.length}슬롯
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 날짜 편집 모달 */}
        {editingDate && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center" onClick={closeEdit}>
            <div
              className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl p-6 animate-slide-up max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-extrabold text-gray-900">
                  {editingDate} 설정
                </h2>
                <button onClick={closeEdit} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>

              {/* 상태 선택 */}
              <div className="mb-5">
                <label className="block text-xs font-extrabold text-gray-700 mb-2">상태</label>
                <div className="flex gap-2">
                  {[
                    { value: 'available' as const, label: '근무 가능', icon: 'ri-check-line', color: '#2E7D6B' },
                    { value: 'unavailable' as const, label: '근무 불가', icon: 'ri-close-line', color: '#D32F2F' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setEditStatus(opt.value)}
                      className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-1.5 ${
                        editStatus === opt.value
                          ? 'border-current text-white'
                          : 'border-gray-200 text-gray-500 bg-white'
                      }`}
                      style={
                        editStatus === opt.value
                          ? { backgroundColor: opt.color, borderColor: opt.color }
                          : {}
                      }
                    >
                      <i className={opt.icon} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 시간대 선택 (가능 상태일 때만) */}
              {editStatus === 'available' && (
                <div className="mb-5">
                  <label className="block text-xs font-extrabold text-gray-700 mb-2">
                    가능 시간대 <span className="text-[10px] text-gray-400 font-medium">(선택)</span>
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {TIME_SLOTS.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => toggleSlot(slot)}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-medium border cursor-pointer transition-all whitespace-nowrap ${
                          editSlots.includes(slot)
                            ? 'border-[#2E7D6B] bg-[#E8F5E9] text-[#2E7D6B]'
                            : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 메모 */}
              <div className="mb-5">
                <label className="block text-xs font-extrabold text-gray-700 mb-2">메모</label>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder="비고사항을 입력하세요 (선택)"
                  rows={2}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white resize-none"
                />
              </div>

              {/* 메시지 */}
              {message && (
                <div className={`mb-4 p-3 rounded-xl text-xs font-bold text-center ${
                  message.includes('오류') ? 'bg-red-50 text-red-500' : 'bg-green-50 text-[#2E7D6B]'
                }`}>
                  {message}
                </div>
              )}

              {/* 저장 버튼 */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
                style={{ backgroundColor: '#1976D2' }}
              >
                {saving ? '저장 중...' : '저장하기'}
              </button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </div>
    </AppLayout>
  );
}